import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Select,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
} from "@fluentui/react-icons";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import {
  deleteAttachment,
  getDownloadSignedUrl,
  updateAttachment,
} from "apps/decooda/decoodaapi";
import { IAttachment } from "apps/decooda/components/projects/projectCard.types";
import { getStatusDisplayText } from "apps/decooda/components/projects/projectConstants";
import { convertDateStringToUnix, formatDate } from "utils/generalUtils";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { useAnalyticsOverviewTabStyles } from "./AnalyticsOverviewTab.styles";
import {
  IAnalyticsOverviewTabProps,
  IMetaCellProps,
} from "./AnalyticsOverviewTab.types";

const getAttachmentBadgeLabel = (filename: string): string => {
  const ext = filename.split(".").pop()?.toUpperCase() ?? "FILE";
  return ext.length > 4 ? ext.slice(0, 4) : ext;
};

const getAttachmentMeta = (att: IAttachment): string => {
  const parts: string[] = [];
  if (att.sheetName) parts.push(`Sheet: ${att.sheetName}`);
  else if (att.delimiter) parts.push(`Delimiter: ${att.delimiter}`);
  if (att.contentColumn) parts.push(`Content: ${att.contentColumn}`);
  return parts.join(" · ");
};

const MetaCell = ({ label, value, fullWidth }: IMetaCellProps): JSX.Element => {
  const styles = useAnalyticsOverviewTabStyles();
  return (
    <div className={fullWidth ? styles.gridCellFullWidth : undefined}>
      <dt className={styles.metaLabel}>{label}</dt>
      {value !== "" ? (
        <dd className={styles.metaValue}>{value}</dd>
      ) : (
        <dd className={styles.metaValueEmpty}>
          {label === "OBJECTIVE" ? "No objective added." : "-"}
        </dd>
      )}
    </div>
  );
};

export const AnalyticsOverviewTab = ({
  project,
  onEdit,
  onAttachmentsChanged,
}: IAnalyticsOverviewTabProps): JSX.Element => {
  const styles = useAnalyticsOverviewTabStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const queryClient = useQueryClient();
  const [deletingAttachment, setDeletingAttachment] =
    useState<IAttachment | null>(null);
  const [editingAttachmentId, setEditingAttachmentId] = useState<string | null>(
    null,
  );
  const [editingColumnValue, setEditingColumnValue] = useState<string>("");
  const [editAttachmentError, setEditAttachmentError] = useState<string>("");

  const attachments = useMemo(
    () => project.attachments ?? [],
    [project.attachments],
  );

  const handleDownloadAttachment = useCallback(async (att: IAttachment) => {
    const result = await getDownloadSignedUrl({ filename: att.fileUrl });
    if (result?.sas) {
      window.open(result.sas, "_blank");
    }
  }, []);

  const handleDeleteRequest = useCallback((att: IAttachment) => {
    setDeletingAttachment(att);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingAttachment) return;
    try {
      await deleteAttachment({
        projectId: project.id,
        attachmentId: deletingAttachment.id,
      });
      void queryClient.invalidateQueries(["analytics-project", project.id]);
      onAttachmentsChanged();
      setDeletingAttachment(null);
    } catch {
      setEditAttachmentError("Failed to delete attachment. Please try again.");
    }
  }, [deletingAttachment, project.id, queryClient, onAttachmentsChanged]);

  const handleDeleteDismiss = useCallback(() => {
    setDeletingAttachment(null);
  }, []);

  const handleStartEditColumn = useCallback((att: IAttachment): void => {
    setEditingAttachmentId(att.id);
    setEditingColumnValue(att.contentColumn || att.columns[0] || "");
    setEditAttachmentError("");
  }, []);

  const handleCancelEditColumn = useCallback((): void => {
    setEditingAttachmentId(null);
    setEditingColumnValue("");
  }, []);

  const handleSaveEditColumn = useCallback(
    async (att: IAttachment): Promise<void> => {
      if (!editingColumnValue) return;
      try {
        await updateAttachment({
          projectId: project.id,
          attachmentId: att.id,
          delimiter: att.delimiter,
          contentColumn: editingColumnValue,
          sheetName: att.sheetName || undefined,
          columns: att.columns,
        });
        queryClient.invalidateQueries(["analytics-project", project.id]);
        onAttachmentsChanged();
        setEditingAttachmentId(null);
        setEditingColumnValue("");
      } catch {
        setEditAttachmentError("Failed to update column. Please try again.");
      }
    },
    [editingColumnValue, project.id, queryClient, onAttachmentsChanged],
  );

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.cardHeaderRow}>
          <h2 className={styles.cardHeader}>BASIC INFORMATION</h2>
          <Button
            appearance="subtle"
            icon={<EditRegular aria-hidden="true" />}
            onClick={onEdit}
            aria-label="Edit analytics project"
          />
        </div>
        <hr className={styles.divider} />
        <dl className={styles.grid}>
          <MetaCell label="ANALYTICS NAME" value={project.name} />
          <MetaCell
            label="CREATED"
            value={formatDate(convertDateStringToUnix(project.created))}
          />
          <MetaCell label="CLIENT NAME" value={project.clientName ?? ""} />
          <MetaCell
            label="CREATED BY"
            value={
              project.createdBy?.displayName ?? project.createdBy?.userId ?? ""
            }
          />
          <MetaCell
            label="PROJECT WORKDAY CODE"
            value={project.workdayCode ?? ""}
          />
          <MetaCell
            label="STATUS"
            value={getStatusDisplayText(project.status)}
          />
          <div />
          <MetaCell
            label="ATTACHMENTS"
            value={
              attachments.length > 0
                ? `${attachments.length} file${attachments.length !== 1 ? "s" : ""}`
                : "None"
            }
          />
          <div />
          <MetaCell label="OBJECTIVE" value={project.objective ?? ""} />
          <div />
        </dl>
      </div>

      <div className={styles.attachmentPanel}>
        <div className={styles.cardHeaderRow}>
          <h2 className={styles.cardHeader}>ATTACHMENTS</h2>
        </div>
        <hr className={styles.divider} />
        {attachments.length === 0 ? (
          <p className={styles.attachmentEmptyText}>
            No attachments added yet.
          </p>
        ) : (
          <ul
            className={styles.attachmentList}
            aria-label="Project attachments"
          >
            {attachments.map((att) => (
              <li key={att.id} className={styles.attachmentRow}>
                <div className={styles.attachmentBadge} aria-hidden="true">
                  {getAttachmentBadgeLabel(att.filename)}
                </div>
                <div className={styles.attachmentInfo}>
                  <p className={styles.attachmentName}>{att.filename}</p>
                  {editingAttachmentId === att.id ? (
                    <>
                      <div className={styles.attachmentEditRow}>
                        <Select
                          value={editingColumnValue}
                          onChange={(_, data) =>
                            setEditingColumnValue(data.value)
                          }
                          size="small"
                          aria-label="Content column"
                          className={styles.attachmentEditSelect}
                        >
                          {att.columns.map((col) => (
                            <option key={col} value={col}>
                              {col}
                            </option>
                          ))}
                        </Select>
                        <Tooltip
                          content="Save column"
                          relationship="label"
                          showDelay={300}
                          hideDelay={100}
                        >
                          <Button
                            appearance="subtle"
                            icon={<CheckmarkRegular aria-hidden="true" />}
                            aria-label="Save column"
                            disabled={!editingColumnValue}
                            onClick={() => void handleSaveEditColumn(att)}
                          />
                        </Tooltip>
                        <Tooltip
                          content="Cancel"
                          relationship="label"
                          showDelay={300}
                          hideDelay={100}
                        >
                          <Button
                            appearance="subtle"
                            icon={<DismissRegular aria-hidden="true" />}
                            aria-label="Cancel editing"
                            onClick={handleCancelEditColumn}
                          />
                        </Tooltip>
                      </div>
                      {editAttachmentError && (
                        <span className={styles.attachmentEditError}>
                          {editAttachmentError}
                        </span>
                      )}
                    </>
                  ) : (
                    <p className={styles.attachmentMeta}>
                      {getAttachmentMeta(att)}
                    </p>
                  )}
                </div>
                {editingAttachmentId !== att.id && (
                  <div className={styles.attachmentActions}>
                    {att.columns.length > 0 && (
                      <Tooltip
                        content="Edit column"
                        relationship="label"
                        showDelay={300}
                        hideDelay={100}
                      >
                        <Button
                          appearance="subtle"
                          icon={<EditRegular aria-hidden="true" />}
                          aria-label={`Edit column for ${att.filename}`}
                          onClick={() => handleStartEditColumn(att)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip
                      content="Download attachment"
                      relationship="label"
                      showDelay={300}
                      hideDelay={100}
                    >
                      <Button
                        appearance="subtle"
                        icon={<ArrowDownloadRegular aria-hidden="true" />}
                        aria-label={`Download ${att.filename}`}
                        onClick={() => handleDownloadAttachment(att)}
                      />
                    </Tooltip>
                    <Tooltip
                      content="Delete attachment"
                      relationship="label"
                      showDelay={300}
                      hideDelay={100}
                    >
                      <Button
                        appearance="subtle"
                        icon={<DeleteRegular aria-hidden="true" />}
                        aria-label={`Delete ${att.filename}`}
                        onClick={() => handleDeleteRequest(att)}
                      />
                    </Tooltip>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog
        open={deletingAttachment !== null}
        onOpenChange={handleDeleteDismiss}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete attachment</DialogTitle>
            <DialogContent>
              Are you sure you want to delete{" "}
              <strong>{deletingAttachment?.filename}</strong>? This cannot be
              undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="subtle" onClick={handleDeleteDismiss}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                className={sharedButtonStyles.dangerButton}
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
